import { useEffect, useRef, useState } from "react";
import { Box, Flex, Text, VStack } from "@chakra-ui/react";
import { FieldValues, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Drawer, Toggle, Tooltip } from "barte-design-system";
import { Input } from "@/components/Forms/Input";
import { DocumentInput } from "@/components/Forms/DocumentInput";
import { PhoneInput } from "@/components/Forms/PhoneInput";
import { InfoIcon } from "@/styles/icons/InfoIcon";
import { useClient } from "@/hooks";
import { useAsyncFunction } from "@/hooks/useAsyncFunction";
import { ClientsCreateData } from "@/models";
import { cleanDocument, clearMask, isValidCnpj, isValidCpf, showToast } from "@/utils";

const createClientSchema = yup.object().shape({
  nameBuyer: yup.string().required("Preenchimento obrigatório!"),
  document: yup.string().required("Preenchimento obrigatório!"),
  documentType: yup.string().required("Preenchimento obrigatório!"),
  documentNation: yup.string().required("Preenchimento obrigatório!"),
  countryCode: yup.string().required("Preenchimento obrigatório!"),
  phone: yup.string().required("Preenchimento obrigatório!"),
  email: yup.string().required("Preenchimento obrigatório!").email("Email inválido"),
  zipCode: yup.string().when("$isUseMyAddress", {
    is: true,
    then: (schema) => schema.nullable(),
    otherwise: (schema) => schema.required("Preenchimento obrigatório!"),
  }),
  street: yup.string().when("$isUseMyAddress", {
    is: true,
    then: (schema) => schema.nullable(),
    otherwise: (schema) => schema.required("Preenchimento obrigatório!"),
  }),
  number: yup.string().when("$isUseMyAddress", {
    is: true,
    then: (schema) => schema.nullable(),
    otherwise: (schema) => schema.required("Preenchimento obrigatório!"),
  }),
  complement: yup.string().nullable(),
  district: yup.string().when("$isUseMyAddress", {
    is: true,
    then: (schema) => schema.nullable(),
    otherwise: (schema) => schema.required("Preenchimento obrigatório!"),
  }),
  city: yup.string().when("$isUseMyAddress", {
    is: true,
    then: (schema) => schema.nullable(),
    otherwise: (schema) => schema.required("Preenchimento obrigatório!"),
  }),
  state: yup.string().when("$isUseMyAddress", {
    is: true,
    then: (schema) => schema.nullable(),
    otherwise: (schema) => schema.required("Preenchimento obrigatório!"),
  }),
  country: yup.string().when("$isUseMyAddress", {
    is: true,
    then: (schema) => schema.nullable(),
    otherwise: (schema) => schema.required("Preenchimento obrigatório!"),
  }),
});

const buyerFields = ["nameBuyer", "document", "email", "phone"];
const addressFields = ["zipCode", "street", "number", "district", "city", "state", "country"];

const defaultFormValues: FieldValues = {
  documentType: "cpf",
  documentNation: "BR",
  countryCode: "+55",
};

interface UseClientFormParams {
  isOpen: boolean;
  onSuccess: () => void;
}

function useClientForm({ isOpen, onSuccess }: UseClientFormParams) {
  const { consultCep, consultCNPJ, postClient } = useClient();
  const { execute: createClient, loading } = useAsyncFunction(postClient);
  const { execute: fetchCep, loading: isCepLoading } = useAsyncFunction(consultCep);
  const [section, setSection] = useState(1);
  const [isUseMyAddress, setIsUseMyAddress] = useState(false);
  const [isCpfValidate, setIsCpfValidate] = useState(false);
  const [isCnpjValidate, setIsCnpjValidate] = useState(false);
  const shouldRevalidateAddressRef = useRef(false);

  const form = useForm({
    resolver: yupResolver(createClientSchema),
    context: { isUseMyAddress },
    mode: "onBlur",
    defaultValues: defaultFormValues,
  });
  const { watch, setValue, setError, clearErrors, trigger, handleSubmit, reset } = form;

  const documentValue = watch("document");
  const documentType = watch("documentType");
  const documentNation = watch("documentNation");
  const cep = watch("zipCode");

  function resetForm() {
    reset(defaultFormValues);
    setSection(1);
    setIsUseMyAddress(false);
    setIsCpfValidate(false);
    setIsCnpjValidate(false);
  }

  function handleToggleUseMyAddress(checked: boolean) {
    clearErrors();
    shouldRevalidateAddressRef.current = true;
    setIsUseMyAddress(checked);
  }

  async function handleGetCep() {
    try {
      clearErrors("zipCode");
      const { data } = await fetchCep(clearMask(cep));
      setValue("street", data.logradouro);
      setValue("district", data.bairro);
      setValue("city", data.localidade);
      setValue("state", data.uf);
      setValue("complement", data.complemento);
      setValue("country", "BR");
    } catch {
      if (cep.length !== 10) return setError("zipCode", { type: "custom", message: "Cep inválido." });
    }
  }

  async function handleGetCnpj() {
    try {
      const { data } = await consultCNPJ(cleanDocument(documentValue));
      setValue("nameBuyer", data?.companyName);
      setValue("fantasyName", data?.fantasyName);
      setValue("street", data?.address?.street);
      setValue("district", data?.address?.district);
      setValue("city", data?.address?.city);
      setValue("state", data?.address?.state);
      setValue("country", data?.address?.country);
      setValue("zipCode", data?.address?.zipCode);
      return true;
    } catch {
      showToast({ message: "Não foi possível consultar o CNPJ. Tente novamente.", type: "error" });
      return false;
    }
  }

  async function handleValidateCpfCnpj() {
    if (documentNation !== "BR") {
      clearErrors("document");
      return;
    }
    clearErrors("document");
    if (documentType === "cpf" && isValidCpf(documentValue)) return setIsCpfValidate(true);
    if (documentType === "cnpj" && isValidCnpj(documentValue)) {
      const success = await handleGetCnpj();
      if (success) setIsCnpjValidate(true);
      return;
    }
    setError("document", { type: "custom", message: "Inclua um CNPJ ou CPF válido" });
  }

  const onSubmit: SubmitHandler<ClientsCreateData> = async (values) => {
    const clientData = {
      document: {
        documentNumber: cleanDocument(values.document),
        documentType: values.documentType,
        documentNation: values.documentNation,
      },
      name: values.nameBuyer,
      email: values.email,
      countryCode: values.countryCode,
      phone: clearMask(values.phone),
      alternativeEmail: values.alternativeEmail,
      address: isUseMyAddress ? null : {
        zipCode: clearMask(values.zipCode),
        street: values.street,
        number: values.number,
        complement: values.complement,
        district: values.district,
        city: values.city,
        state: values.state,
        country: values.country,
      },
    };
    try {
      await createClient(clientData);
      showToast({ message: "Cliente cadastrado com sucesso!", type: "success" });
      resetForm();
      onSuccess();
    } catch (err) {
      const apiErrors = err?.response?.data?.errors;
      if (apiErrors?.length) {
        apiErrors.forEach((item) => {
          showToast({ message: item.description, type: "error" });
        });
      } else {
        showToast({ message: "Não foi possível cadastrar o cliente. Tente novamente.", type: "error" });
      }
    }
  };

  const sections = {
    BACK() {
      setSection(section - 1);
    },
    async NEXT() {
      if (section === 1) {
        const isValid = await trigger(buyerFields);
        if (isValid) setSection(section + 1);
      } else {
        setSection(section + 1);
      }
    },
    LAST() {
      handleSubmit(onSubmit)();
    },
  };

  useEffect(() => {
    setIsCnpjValidate(false);
    setIsCpfValidate(false);
  }, [documentValue]);

  useEffect(() => {
    if (cep?.length >= 9) handleGetCep();
  }, [cep]);

  useEffect(() => {
    if (!shouldRevalidateAddressRef.current) return;
    shouldRevalidateAddressRef.current = false;
    trigger(addressFields);
  }, [isUseMyAddress]);

  useEffect(() => {
    if (isOpen) resetForm();
  }, [isOpen]);

  return {
    form,
    section,
    setSection,
    documentType,
    documentNation,
    isUseMyAddress,
    handleToggleUseMyAddress,
    isCepLoading,
    isCpfValidate,
    isCnpjValidate,
    loading,
    handleValidateCpfCnpj,
    sections,
  };
}

type ClientFormReturn = ReturnType<typeof useClientForm>;

function ClientFormFields({
  form,
  section,
  documentType,
  isUseMyAddress,
  handleToggleUseMyAddress,
  isCepLoading,
  handleValidateCpfCnpj,
}: ClientFormReturn) {
  const { register, setValue, getValues, formState: { errors } } = form;

  if (section === 1) {
    return (
      <VStack spacing={4} align="stretch" w="100%">
        <DocumentInput
          name="document"
          fixedLabel="Documento "
          register={{ ...register("document") }}
          setValue={setValue}
          getValues={getValues}
          errors={errors?.document}
          height="40px"
          fontSize="1rem"
          onBlurCapture={handleValidateCpfCnpj}
          mask={documentType}
        />
        <Input
          name="nameBuyer"
          placeholder={documentType === "cnpj" ? "Razão Social" : "Nome do cliente"}
          label={documentType === "cnpj" ? "Razão Social" : "Nome do cliente"}
          type="text"
          register={{ ...register("nameBuyer") }}
          errors={errors?.nameBuyer}
        />
        <PhoneInput
          name="phone"
          label="Telefone Celular"
          type="cellphone"
          register={{ ...register("phone") }}
          setValue={setValue}
          defaultCountryCodeValue="+55"
          mask="cellphone"
          errors={errors?.phone}
        />
        <Input
          name="email"
          placeholder="Email"
          label="Email"
          type="text"
          register={{ ...register("email") }}
          errors={errors?.email}
        />
        <Input
          name="alternativeEmail"
          placeholder="Email financeiro (opcional)"
          label="Email financeiro (opcional)"
          type="text"
          register={{ ...register("alternativeEmail") }}
        />
      </VStack>
    );
  }

  return (
    <VStack spacing={4} align="stretch" w="100%">
      <Flex align="center" gap="2">
        <Text fontSize="sm" color="gray.600">
          Usar o meu endereço no lugar do endereço do cliente
        </Text>
        <Tooltip
          placement="bottom"
          label="Utilizaremos o seu endereço no lugar do endereço do cliente na emissão de boletos e Notas Fiscais"
        >
          <span>
            <InfoIcon size={14} color="#000000" opacity="0.4" />
          </span>
        </Tooltip>
        <Box ml="auto">
          <Toggle
            checked={isUseMyAddress}
            onChange={handleToggleUseMyAddress}
          />
        </Box>
      </Flex>
      <Input
        name="zipCode"
        label="CEP"
        type="text"
        register={{ ...register("zipCode") }}
        mask="cep"
        errors={errors?.zipCode}
        containerDisabled={!isUseMyAddress}      />
      <Input
        name="street"
        label="Rua"
        type="text"
        register={{ ...register("street") }}
        containerDisabled={!(isCepLoading || isUseMyAddress)}
        errors={errors?.street}      />
      <Input
        name="number"
        label="Número"
        type="text"
        register={{ ...register("number") }}
        containerDisabled={!(isCepLoading || isUseMyAddress)}
        errors={errors?.number}      />
      <Input
        name="complement"
        label="Complemento (opcional)"
        type="text"
        register={{ ...register("complement") }}
        containerDisabled={!(isCepLoading || isUseMyAddress)}      />
      <Input
        name="district"
        label="Bairro"
        type="text"
        register={{ ...register("district") }}
        containerDisabled={!(isCepLoading || isUseMyAddress)}
        errors={errors?.district}      />
      <Input
        name="city"
        label="Cidade"
        type="text"
        register={{ ...register("city") }}
        containerDisabled={!(isCepLoading || isUseMyAddress)}
        errors={errors?.city}      />
      <Input
        name="state"
        label="Estado"
        type="text"
        maxLength={3}
        placeholder="Ex: SP"
        textTransform="uppercase"
        register={{ ...register("state") }}
        containerDisabled={!(isCepLoading || isUseMyAddress)}
        errors={errors?.state}      />
      <Input
        name="country"
        label="País"
        type="text"
        placeholder="Ex: BR"
        textTransform="uppercase"
        register={{ ...register("country") }}
        containerDisabled={!(isCepLoading || isUseMyAddress)}
        errors={errors?.country}      />
    </VStack>
  );
}

interface ClientFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function ClientFormDrawer({ isOpen, onClose, onSuccess }: ClientFormDrawerProps) {
  const clientForm = useClientForm({ isOpen, onSuccess });
  const { section, setSection, documentType, documentNation, isCpfValidate, isCnpjValidate, loading, sections } = clientForm;

  const isNextDisabled = documentNation === "BR"
    ? (documentType === "cpf" ? !isCpfValidate : !isCnpjValidate)
    : false;

  const primaryButton = section === 1
    ? { children: "Próximo", onClick: () => sections.NEXT(), disabled: isNextDisabled }
    : { children: loading ? "Salvando..." : "Salvar", onClick: () => sections.LAST(), loading };

  return (
    <Drawer
      isOpen={isOpen}
      title="Adicionar cliente"
      onClose={onClose}
      onBack={section === 2 ? () => setSection(1) : undefined}
      footer={{
        primaryButton,
        secondaryButton: {
          children: "Cancelar",
          onClick: onClose,
          variant: "outline",
        },
      }}
    >
      <ClientFormFields {...clientForm} />
    </Drawer>
  );
}
